import React from "react";
import { useState, useEffect } from "react";
import { Tabs, Button, Row, Col, Space, Spin, message } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import { getUserById } from "@/ts/retrieve-data";
import { showToast } from "@/ts/modals";
import { IUserRecord, ParentModule } from "@/ts/common-types";
import { UserTabs } from "@/ts/common-types";
import Layout from "@/components/wrappers/layouts/main-layout/Layout";
import { LeftOutlined, CopyOutlined } from "@ant-design/icons";
import UserDetails from "./UserDetails";
import UserAccess from "./UserAccess";
import UserActivityLogs from "./UserActivityLogs";
import "@/scss/admin-view.scss";

const { TabPane } = Tabs;

const tabContentStyle: React.CSSProperties = {
    height: "calc(100vh - 300px)",
    overflowY: "auto",
    overflowX: "hidden",
    padding: "20px 0",
    cursor: "default",
};

const UserView = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<UserTabs>(UserTabs.DETAILS);
    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState<IUserRecord | null>(null);
    const [tabLoading, setTabLoading] = useState(false);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const user = await getUserById(String(userId));

                if (!user) {
                    showToast("danger", "User not found");
                    navigate(
                        `/${ParentModule.ADMIN_SETTINGS}/settings/users`
                    );
                    return;
                }

                setUserData(user);
            } catch (error: any) {
                showToast("danger", error.message || "Failed to load user");
                navigate(`/${ParentModule.ADMIN_SETTINGS}/settings/users`);
            } finally {
                setLoading(false);
            }
        };

        if (userId) {
            fetchUserData();
        }
    }, [userId, navigate]);

    const handleCopyUID = () => {
        if (userData?.uid) {
            navigator.clipboard.writeText(userData.uid);
            message.success("UID copied to clipboard");
        }
    };

    if (loading)
        return (
            <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "100vh" }}
            >
                <Spin size="large" />
            </div>
        );
    if (!userData) return null;

    return (
        <Layout>
            <div className="client-view-container">
                <div className="card shadow">
                    <div className="p-4" style={{ cursor: "default" }}>
                        <Row
                            className="mb-4"
                            justify="space-between"
                            align="middle"
                        >
                            <Col>
                                <Space size="middle">
                                    <Button
                                        onClick={() =>
                                            navigate(
                                                `/${ParentModule.ADMIN_SETTINGS}/settings/users`
                                            )
                                        }
                                        type="default"
                                        className="d-flex align-items-center"
                                        style={{ gap: "4px" }}
                                    >
                                        <LeftOutlined /> Back to Users
                                    </Button>
                                </Space>
                            </Col>
                        </Row>

                        <div className="mb-4">
                            <h2 className="mb-2">
                                {userData.user_id} - {userData.first_name} {userData.last_name}
                            </h2>
                            <div className="d-flex align-items-center gap-2">
                            <span className="text-muted small">
                                <span className="text-dark fw-bold">UID:</span> {userData.uid}
                            </span>

                                <Button
                                    type="text"
                                    size="small"
                                    icon={<CopyOutlined />}
                                    onClick={handleCopyUID}
                                    className="p-1"
                                    title="Copy UID"
                                />
                            </div>
                        </div>

                        <Tabs
                            activeKey={activeTab}
                            onChange={(key) =>
                                setActiveTab(key as UserTabs)
                            }
                            className="client-view-tabs"
                            size="large"
                        >
                            <TabPane tab="Details" key={UserTabs.DETAILS}>
                                <div style={tabContentStyle}>
                                    {tabLoading ? (
                                        <div className="text-center">
                                            <Spin />
                                        </div>
                                    ) : (
                                        <UserDetails user={userData} />
                                    )}
                                </div>
                            </TabPane>
                            <TabPane tab="Access" key={UserTabs.ACCESS}>
                                <div style={tabContentStyle}>
                                    {userData && (
                                        <UserAccess user={userData} />
                                    )}
                                </div>
                            </TabPane>
                            <TabPane tab="Activity Logs" key={UserTabs.ACTIVITY_LOGS}>
                                <div style={tabContentStyle}>
                                    {userData && (
                                        <UserActivityLogs userId={userData.user_id} />
                                    )}
                                </div>
                            </TabPane>
                        </Tabs>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default UserView;